import _ from 'lodash';
import React from 'react';

const keyMaps = {
  name: 'label',
  title: 'label',
  id: 'value',
};

const mapper = d => _.mapKeys(d, (v, k) => keyMaps[k] || k);

const optionMapper = o => ({ label: o, value: o });

const setFund = fund => {
  const reFund = {
    ...fund,
    fundType: optionMapper(fund.fundType),
    company: mapper(fund.company),
    fundAssetClass: mapper(fund.fundAssetClass),
    series: fund.series.map(mapper),
    members: fund.members.map(mapper),
    monthlyUpdateUrlType: optionMapper(fund.monthlyUpdateUrlType),
    performanceFeeCharged: optionMapper(fund.performanceFeeCharged),
    stockCode: mapper(fund.stockCode),
  };

  return reFund;
};

const setCompanyOptions = (s, payload) => {
  const cList = _.get(payload, 'companies', []);
  return {
    ...s,
    companyOptions: cList.map(mapper),
    membersOptions: _.get(_.find(cList, { id: _.get(s, 'fund.company.value') }), 'members', []).map(mapper),
  };
};

const setStockCodeOptions = (s, payload) => ({
  ...s,
  stockCodeOptions: _.get(payload, 'stockCodes', []).map(mapper),
});

const setFundAssetClassOptions = (s, payload) => ({
  ...s,
  fundAssetClassOptions: _.get(payload, 'fundAssetClasses', []).map(mapper),
});

const setCompany = (s, payload) => {
  const hasCompanyChanged = _.get(s, 'fund.company.value') !== payload.value;

  return {
    ...s,
    ...(hasCompanyChanged ? { manager: null } : {}),
    fund: {
      ...s.fund,
      ...(hasCompanyChanged ? { members: [] } : {}),
      company: payload,
    },
    membersOptions: _.get(payload, 'members', []).map(mapper),
  };
};

const setQandas = (s, { questions, answers }) => ({
  ...s,
  qandas: _(questions).sortBy('order').map(q => {
    const a = _.find(_.get(answers, 'qandas'), { question: q.description });
    return {
      ..._.pick(q, ['order', 'category', 'fundTypes']),
      qandaId: _.get(a, 'id', null),
      question: q.description,
      questionId: q.id,
      answer: _.get(a, 'answer', ''),
    };
  }).value(),
});

const setQandaAnswer = (s, { questionId, answer }) => ({
  ...s,
  qandas: s.qandas.map(q => ({ ...q, ...(q.questionId === questionId ? { answer } : {}) })),
});

const setQandaIds = (s, payload) => ({
  ...s,
  qandas: s.qandas.map(q => {
    const match = _.find(payload, { questionId: q.questionId });
    return { ...q, ...(match ? { id: match.qandaId || match.id } : {}) };
  }),
});

const setSeriesOptions = (s, payload) => {
  const options = _.get(payload, 'series', []);

  return {
    ...s,
    seriesOptions: options.map(mapper),
  };
};

// eslint-disable-next-line complexity
const reducer = (state, { type, payload }) => {
  switch (type) {
    case 'setId':
      return { ...state, fund: { ...state.fund, id: payload } };
    case 'setName':
      return { ...state, fund: { ...state.fund, name: payload } };
    case 'setFundtype':
      return { ...state, fund: { ...state.fund, fundType: payload } };
    case 'setApircode':
      return { ...state, fund: { ...state.fund, apirCode: payload } };
    case 'setIsin':
      return { ...state, fund: { ...state.fund, isin: payload } };
    case 'setDataproviderid':
      return { ...state, fund: { ...state.fund, dataProviderId: payload } };
    case 'setDpPdsFileUrl':
      return { ...state, fund: { ...state.fund, dpPdsFileUrl: payload } };
    case 'setStockcode':
      return { ...state, fund: { ...state.fund, stockCode: payload } };
    case 'setInceptiondate':
      return { ...state, fund: { ...state.fund, inceptionDate: payload } };
    case 'setObjective':
      return { ...state, fund: { ...state.fund, objective: payload } };
    case 'setGeographicalfocus':
      return { ...state, fund: { ...state.fund, geographicalFocus: payload } };
    case 'setInvestmenthorizon':
      return { ...state, fund: { ...state.fund, investmentHorizon: payload } };
    case 'setMinimuminvestment':
      return { ...state, fund: { ...state.fund, minimumInvestment: payload } };
    case 'setFundsize':
      return { ...state, fund: { ...state.fund, fundSize: payload } };
    case 'setFundsizedate':
      return { ...state, fund: { ...state.fund, fundSizeDate: payload } };
    case 'setCurrency':
      return { ...state, fund: { ...state.fund, currency: payload } };
    case 'setLegalstructure':
      return { ...state, fund: { ...state.fund, legalStructure: payload } };
    case 'setDomicile':
      return { ...state, fund: { ...state.fund, domicile: payload } };
    case 'setInvesturl':
      return { ...state, fund: { ...state.fund, investUrl: payload } };
    case 'setFundpageurl':
      return { ...state, fund: { ...state.fund, fundPageUrl: payload } };
    case 'setPdsfileurl':
      return { ...state, fund: { ...state.fund, pdsFileUrl: payload } };
    case 'setMonthlyupdatefileurl':
      return { ...state, fund: { ...state.fund, monthlyUpdateFileUrl: payload } };
    case 'setMonthlyupdateurltype':
      return { ...state, fund: { ...state.fund, monthlyUpdateUrlType: payload } };
    case 'setTmdfileurl':
      return { ...state, fund: { ...state.fund, tmdFileUrl: payload } };
    case 'setCompany':
      return setCompany(state, payload);
    case 'setFundassetclass':
      return { ...state, fund: { ...state.fund, fundAssetClass: payload } };
    case 'setMembers':
      return { ...state, fund: { ...state.fund, members: payload } };
    case 'setDisclaimer':
      return { ...state, fund: { ...state.fund, disclaimer: payload } };
    case 'setCompanyOptions':
      return setCompanyOptions(state, payload);
    case 'setStockCodeOptions':
      return setStockCodeOptions(state, payload);
    case 'setFundAssetClassOptions':
      return setFundAssetClassOptions(state, payload);
    case 'setFeaturedWireUrls':
      return { ...state, fund: { ...state.fund, featuredWireUrls: payload } };
    case 'setQandas':
      return setQandas(state, payload);
    case 'setQandaAnswer':
      return setQandaAnswer(state, payload);
    case 'setQandaIds':
      return setQandaIds(state, payload);
    case 'setManager':
      return { ...state, manager: payload };
    case 'setPerformance':
      return { ...state, fund: { ...state.fund, performance: payload } };
    case 'setDpPerformance':
      return { ...state, fund: { ...state.fund, dpPerformance: payload } };
    case 'setManagementfee':
      return { ...state, fund: { ...state.fund, managementFee: payload } };
    case 'setPerformancefeecharged':
      return { ...state, fund: { ...state.fund, performanceFeeCharged: payload } };
    case 'setPerformancefeepds':
      return { ...state, fund: { ...state.fund, performanceFeePds: payload } };
    case 'setPerformancefee':
      return { ...state, fund: { ...state.fund, performanceFee: payload } };
    case 'setPerformancefeemethodology':
      return { ...state, fund: { ...state.fund, performanceFeeMethodology: payload } };
    case 'setTotalcostratio':
      return { ...state, fund: { ...state.fund, totalCostRatio: payload } };
    case 'setFinancialyearendfeesdate':
      return { ...state, fund: { ...state.fund, financialYearEndFeesDate: payload } };
    case 'setBuyspread':
      return { ...state, fund: { ...state.fund, buySpread: payload } };
    case 'setSellspread':
      return { ...state, fund: { ...state.fund, sellSpread: payload } };
    case 'setPdsfeesdate':
      return { ...state, fund: { ...state.fund, pdsFeesDate: payload } };
    case 'setLatestprospectusdate':
      return { ...state, fund: { ...state.fund, latestProspectusDate: payload } };
    case 'setDayendentryprice':
      return { ...state, fund: { ...state.fund, dayEndEntryPrice: payload } };
    case 'setDayendexitprice':
      return { ...state, fund: { ...state.fund, dayEndExitPrice: payload } };
    case 'setDayendnavprice':
      return { ...state, fund: { ...state.fund, dayEndNavPrice: payload } };
    case 'setDayendpricingupdatedate':
      return { ...state, fund: { ...state.fund, dayEndPricingUpdateDate: payload } };
    case 'setDayendmarketprice':
      return { ...state, fund: { ...state.fund, dayEndMarketPrice: payload } };
    case 'setDayendmarketpricedate':
      return { ...state, fund: { ...state.fund, dayEndMarketPriceDate: payload } };
    case 'setDayendnav':
      return { ...state, fund: { ...state.fund, dayEndNav: payload } };
    case 'setDayendnavdate':
      return { ...state, fund: { ...state.fund, dayEndNavDate: payload } };
    case 'setDpFees':
      return {
        ...state,
        fund: {
          ...state.fund,
          ...payload,
          performanceFeeCharged: optionMapper(payload.performanceFeeCharged),
        },
      };
    case 'setDpNetAssets':
      return { ...state, fund: { ...state.fund, ...payload } };
    case 'setSeriesOptions':
      return setSeriesOptions(state, payload);
    case 'setSeries':
      return { ...state, fund: { ...state.fund, series: payload } };
    case 'setWholesale':
      return { ...state, fund: { ...state.fund, wholesale: payload } };
    case 'showDataReviewModal':
      return {
        ...state,
        dataReview: payload,
        showDataReviewModal: true,
      };
    case 'dismissDataReviewModal':
      return {
        ...state,
        showDataReviewModal: false,
      };
    case 'dataReviewApproved':
      return {
        ...state,
        fund: {
          ...state.fund,
          dataReviews: _.reject(state.fund.dataReviews, { id: payload.reviewId }),
        },
        showDataReviewModal: false,
      };
    case 'setErrors':
      return { ...state, errors: payload };
    default:
      throw new Error();
  }
};

const buildDefaultState = fund => ({
  fund: setFund(fund),
  companyOptions: [],
  fundAssetClassOptions: [],
  stockCodeOptions: [],
  membersOptions: [],
  investmentHorizonOptions: [
    {
      label: null,
      value: null,
    },
    {
      label: '0-3 years',
      value: '0-3 years',
    },
    {
      label: '3+ years',
      value: '3+ years',
    },
    {
      label: '3-5 years',
      value: '3-5 years',
    },
    {
      label: '5+ years',
      value: '5+ years',
    },
    {
      label: '5-7 years',
      value: '5-7 years',
    },
    {
      label: '7+ years',
      value: '7+ years',
    },
  ],
  performanceFeeChargedOptions: ['yes', 'no', 'unknown'].map(optionMapper),
  fundTypeOptions: [null, 'ETF', 'LIC', 'LIT', 'Managed Fund'].map(optionMapper),
  monthlyUpdateUrlTypeOptions: [null, 'Static URL', 'Unique URL'].map(optionMapper),
  qandas: [],
  manager: fund.members.map(mapper).filter(m => m.role === 'manager')[0] || null,
  seriesOptions: [],
  dataReview: null,
  showDataReviewModal: false,
  errors: {},
});

const FundContext = React.createContext();

export default FundContext;
export {
  buildDefaultState,
  reducer,
};
